import { Component, type ReactNode } from 'react';

/**
 * Lightweight error boundary for WebGL Canvas components.
 * On shader failure or context loss, shows a dark background instead of
 * crashing the entire page. WebGL errors are silent (no throw) — they
 * produce black screens. This boundary catches actual JS errors in the
 * React component tree above the Canvas (r3f errors, uniform mismatches,
 * null texture references, etc.).
 */
export class CanvasErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('[Canvas error]', error.message);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="w-full h-full bg-black" />
      );
    }
    return this.props.children;
  }
}
